/**
 * Pure rules for a composition file uploaded into a quantity composition. A file can mix the two
 * columns: a row's `Quantity` is what counts, and a row with no quantity is sized by its `Weight`.
 * A row the composition already holds is sized again by the file's value only when the user gave the
 * file priority; a new row enters like any added instrument (checked, then priced) and carries the
 * file's value until it can be sized by it.
 * Validating the values is the save's job, not this module's. Nothing here calls the API or touches
 * the builder.
 */
import type { EditorVerifyExcelRequestPolicyEnum } from "@eu.mdotm.api/sphere";
import type { OrderedMap } from "immutable";
import type { Instrument } from "../../const";
import { canSize, type SizingEdit } from "../position-sizing/sizing";

/**
 * What an upload adds to a row. One named block for the same reason as the other row states in
 * `../../const`: dropping the feature drops this type, and the compiler names every reader.
 */
export type InstrumentUploadedQuantityAttributes = {
	/** The file's value for a row added by the upload, waiting until the row can be sized by it. */
	uploadedQuantity?: SizingEdit;
};

export type QuantityUploadPlan = {
	/** Rows the composition does not hold yet, each with the file's value when it gives one. */
	added: Array<{ row: Instrument; edit: SizingEdit | undefined }>;
	/** Rows the composition holds whose value the file changes. */
	resized: Array<{ id: string; edit: SizingEdit }>;
};

/**
 * The value a verified file row asks for: its quantity, else its weight. A weight of 0 is no weight,
 * since the verified file reads an empty Weight cell as 0. A quantity of 0 is a quantity: an empty
 * Quantity cell comes back as none, so a 0 there was written, and sizes the row to no shares.
 */
export function fileSizing(row: Instrument): SizingEdit | undefined {
	if (row.quantity != null) {
		return { field: "QUANTITY", value: row.quantity };
	}
	if (row.weight) {
		return { field: "WEIGHT", value: row.weight };
	}
	return undefined;
}

function holds(row: Instrument, edit: SizingEdit): boolean {
	return (edit.field === "QUANTITY" ? row.quantity : row.weight) === edit.value;
}

/**
 * Splits the verified upload against the composition. A row the composition holds is matched by id,
 * and resized only under `PRIORITY_TO_EXCEL_WEIGHTS`: with the editor's priority it keeps the
 * quantity and weight it has. A deleted row is left as it is, and so is a row the file gives no
 * value, or the value it already has. The cash row is never sized, whatever the file says.
 */
export function planQuantityUpload(
	uploaded: Instrument[],
	composition: OrderedMap<string, Instrument>,
	policy: EditorVerifyExcelRequestPolicyEnum,
): QuantityUploadPlan {
	const plan: QuantityUploadPlan = { added: [], resized: [] };
	for (const row of uploaded) {
		const edit = fileSizing(row);
		const existing = composition.get(row.id);
		if (!existing) {
			plan.added.push({ row, edit });
			continue;
		}
		if (policy !== "PRIORITY_TO_EXCEL_WEIGHTS" || existing.deleted || !edit || holds(existing, edit)) {
			continue;
		}
		plan.resized.push({ id: row.id, edit });
	}
	return plan;
}

/** The row as it waits to be sized by `edit` from the file. */
export function withUploadedQuantity(row: Instrument, edit: SizingEdit | undefined): Instrument {
	return edit ? { ...row, uploadedQuantity: edit } : row;
}

export function withoutUploadedQuantity(row: Instrument): Instrument {
	const { uploadedQuantity: _uploadedQuantity, ...rest } = row;
	return rest;
}

/**
 * The uploaded rows whose file value can go out now: each that can be sized by its file field. Every
 * other row keeps waiting; one that never can be sized (a quantity on a row held by value, a row its
 * check found cannot be held) simply never goes out. The file's verification already rejects a
 * quantity on an instrument held only by weight, so that row reaches here only from the editor's own
 * values.
 */
export function uploadedSizings(rows: Iterable<Instrument>): Array<{ id: string; edit: SizingEdit }> {
	const ready: Array<{ id: string; edit: SizingEdit }> = [];
	for (const row of rows) {
		const edit = row.uploadedQuantity;
		if (edit && !row.deleted && canSize(row, edit.field)) {
			ready.push({ id: row.id, edit });
		}
	}
	return ready;
}
